import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { GalleryManager, ServicesManager, PricingManager } from '../components/admin/ContentManagers';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, LogOut, Download, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const authConfig = { headers: { Authorization: `Basic ${localStorage.getItem('adminAuth')}` } };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.get(`${API}/admin/verify`, {
        auth: {
          username: username,
          password: password
        }
      });

      if (response.data.authenticated) {
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', btoa(`${username}:${password}`));
        toast.success('Login successful!');
        fetchBookings();
      }
    } catch (error) {
      toast.error('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const auth = localStorage.getItem('adminAuth');
      if (!auth) return;

      const [storedUsername, storedPassword] = atob(auth).split(':');

      const response = await axios.get(`${API}/bookings`, {
        auth: {
          username: storedUsername,
          password: storedPassword
        }
      });

      setBookings(response.data);
      setFilteredBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
    setUsername('');
    setPassword('');
    setBookings([]);
    toast.success('Logged out successfully');
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term) {
      setFilteredBookings(bookings);
      return;
    }

    const filtered = bookings.filter(booking =>
      booking.name.toLowerCase().includes(term.toLowerCase()) ||
      booking.email.toLowerCase().includes(term.toLowerCase()) ||
      booking.treatment.toLowerCase().includes(term.toLowerCase()) ||
      booking.phone.includes(term)
    );

    setFilteredBookings(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Treatment', 'Preferred Date', 'Message', 'Status', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...filteredBookings.map(booking =>
        [
          booking.name,
          booking.email,
          booking.phone,
          booking.treatment,
          booking.preferredDate || 'Not specified',
          `"${booking.message || 'No message'}"`,
          booking.status,
          new Date(booking.createdAt).toLocaleString()
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Bookings exported successfully');
  };

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth) {
      setIsAuthenticated(true);
      fetchBookings();
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <Card className="w-full max-w-md border-2 border-[#D4AF37]">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-black">Admin Login</CardTitle>
            <CardDescription className="text-center">Angelina White Aesthetics</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="border-gray-300 focus:border-[#D4AF37]"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-gray-300 focus:border-[#D4AF37]"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-white"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-black">Admin Dashboard</h1>
            <p className="text-gray-600">Manage bookings & website content</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="mb-6 bg-white border border-[#D4AF37]/40">
            <TabsTrigger data-testid="tab-bookings" value="bookings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">Bookings</TabsTrigger>
            <TabsTrigger data-testid="tab-gallery" value="gallery" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">Gallery</TabsTrigger>
            <TabsTrigger data-testid="tab-services" value="services" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">Services</TabsTrigger>
            <TabsTrigger data-testid="tab-pricing" value="pricing" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">Pricing</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <Card className="border-2 border-[#D4AF37] mb-6">
          <CardHeader>
            <CardTitle>Booking Requests ({filteredBookings.length})</CardTitle>
            <CardDescription>View and manage all client booking requests</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, email, phone, or treatment..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 border-gray-300 focus:border-[#D4AF37]"
                />
              </div>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Treatment</TableHead>
                    <TableHead>Preferred Date</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                        No bookings found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell className="font-semibold">{booking.name}</TableCell>
                        <TableCell>
                          <a href={`mailto:${booking.email}`} className="text-[#D4AF37] hover:underline">
                            {booking.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          <a href={`tel:${booking.phone}`} className="text-[#D4AF37] hover:underline">
                            {booking.phone}
                          </a>
                        </TableCell>
                        <TableCell>{booking.treatment}</TableCell>
                        <TableCell>{booking.preferredDate || 'Not specified'}</TableCell>
                        <TableCell className="max-w-xs truncate">{booking.message || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-[#D4AF37] text-[#D4AF37]">
                            {booking.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {new Date(booking.createdAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="gallery">
            <Card className="border-2 border-[#D4AF37] mb-6">
              <CardHeader>
                <CardTitle>Before & After Gallery</CardTitle>
                <CardDescription>Add, edit or remove gallery photos shown on the website</CardDescription>
              </CardHeader>
              <CardContent>
                <GalleryManager API={API} authConfig={authConfig} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services">
            <Card className="border-2 border-[#D4AF37] mb-6">
              <CardHeader>
                <CardTitle>Services</CardTitle>
                <CardDescription>Manage the treatments shown in the services carousel</CardDescription>
              </CardHeader>
              <CardContent>
                <ServicesManager API={API} authConfig={authConfig} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pricing">
            <Card className="border-2 border-[#D4AF37] mb-6">
              <CardHeader>
                <CardTitle>Treatment Pricing</CardTitle>
                <CardDescription>Manage the price list shown on the website</CardDescription>
              </CardHeader>
              <CardContent>
                <PricingManager API={API} authConfig={authConfig} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
